/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import AccountService from 'cio/services/account';
import EnvironmentService from 'cio/services/environment';
import CustomForm from 'cio/models/custom-form';
import { tracked } from '@glimmer/tracking';
import isValidUrl from 'cio/utils/validators/url';
import FormValidations from 'cio/utils/form-validations';
import { getFormsHostForDataCenter } from 'cio/utils/data-centers';

interface FormsCustomIntegrationDetailsHTMLActionArgs {
  form: CustomForm | null;
  onCopy: () => unknown;
  credentials?: {
    siteId?: string;
    apiKey?: string;
  };
}

const validations = {
  newFormID(value: string) {
    if (value.trim() === '') {
      return "can't be blank";
    }

    return true;
  },

  successURL(url: string) {
    if (url.trim() === '') {
      return "can't be blank";
    } else if (!isValidUrl(url.trim())) {
      return 'success page address must be a valid link';
    }

    return true;
  },
};

export default class FormsCustomIntegrationDetailsHTMLAction extends Component<FormsCustomIntegrationDetailsHTMLActionArgs> {
  @service account!: AccountService;
  @service environment!: EnvironmentService;

  private validations!: FormValidations;

  @tracked formValues = {
    newFormID: '',
    successURL: '',
  };

  constructor(owner: unknown, args: FormsCustomIntegrationDetailsHTMLActionArgs) {
    super(owner, args);

    this.validations = FormValidations.create({
      validations,
      context: this,
      modelKey: 'formValues',
    });
  }

  @computed('args.form.formID', 'formValues.newFormID')
  get formId() {
    if (this.args.form == null) {
      return this.formValues.newFormID;
    }

    return this.args.form.formId;
  }

  get formSubmitURL() {
    let hostname = getFormsHostForDataCenter(this.account.current?.dataCenter) ?? '';
    let siteId = this.args.credentials?.siteId;
    let formId = this.formId || '{{form_id}}';

    // eslint-disable-next-line no-useless-escape
    return `${hostname}/forms/submit_action?site_id=${siteId}&form_id=${formId}&success_url=${this.formValues.successURL}`;
  }

  get snippet() {
    // prettier-ignore
    return `
<form
  method="POST"
  action="${this.formSubmitURL}"
>
  <label for="email_input">E-mail address</label>
  <input id="email_input" name="email" type="email" />
  <button>Submit</button>
</form>
`.trim();
  }

  get canCopySnippet() {
    return (
      this.args.credentials != null &&
      this.formId.trim() !== '' &&
      this.formValues.successURL.trim() !== '' &&
      !this.validations.hasErrors
    );
  }

  @action setSuccessURL(url: string) {
    this.formValues = { ...this.formValues, successURL: url.trim() };
    this.validations.validateAttribute('successURL');
  }

  @action setFormID(value: string) {
    this.formValues = { ...this.formValues, newFormID: value.trim() };
    this.validations.validateAttribute('newFormID');
  }

  @action
  generateNewFormID() {
    this.setFormID(window.UUID.generate().split('-').join('').slice(0, 15));
  }
}
